import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronUp, Lightbulb, Send, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import KineticMarquee from "@/components/KineticMarquee";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "topic", label: "topic" },
  { id: "solo", label: "solo episode" },
  { id: "guest", label: "guest idea" },
  { id: "rant", label: "rant request" },
  { id: "other", label: "other" },
];

function getVoterId() {
  let id = localStorage.getItem("tsws_voter_id");
  if (!id) {
    id = `v_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem("tsws_voter_id", id);
  }
  return id;
}

function getVotedSet() {
  try {
    return new Set(JSON.parse(localStorage.getItem("tsws_voted") || "[]"));
  } catch {
    return new Set();
  }
}

export default function Suggest() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", title: "", body: "", category: "topic" });
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState("all");
  const [voted, setVoted] = useState(getVotedSet());
  const voterId = useMemo(() => getVoterId(), []);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/suggestions`);
      setItems(r.data || []);
    } catch (e) {
      // ignore for now
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/suggestions`, form);
      setItems((prev) => [data, ...prev]);
      setForm({ name: "", title: "", body: "", category: "topic" });
      toast.success("Idea added. Get the votes in.");
    } catch (e) {
      toast.error("Couldn't add idea.");
    } finally {
      setSubmitting(false);
    }
  };

  const upvote = async (id) => {
    if (voted.has(id)) return;
    // optimistic
    setItems((prev) =>
      prev
        .map((it) => (it.id === id ? { ...it, upvotes: (it.upvotes || 0) + 1 } : it))
        .sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0))
    );
    const newVoted = new Set(voted);
    newVoted.add(id);
    setVoted(newVoted);
    localStorage.setItem("tsws_voted", JSON.stringify([...newVoted]));
    try {
      await axios.post(`${API}/suggestions/${id}/upvote`, { voter_id: voterId });
    } catch (e) {
      toast.error("Vote didn't go through.");
    }
  };

  const filtered =
    filter === "all" ? items : items.filter((it) => it.category === filter);

  return (
    <div data-testid="suggest-page" className="px-6 lg:px-12 py-16 lg:py-24 pb-32 text-[var(--tsws-green)]">
      <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
        <div className="lg:col-span-8">
          <span className="font-label text-xs text-[var(--tsws-cigar)]">⏤ Solo Pod Suggestions</span>
          <h1 className="font-display text-7xl md:text-8xl lg:text-[10rem] mt-3 leading-[0.82]">
            pitch sav<br />
            an idea<span className="text-[var(--tsws-cigar)]">.</span>
          </h1>
        </div>
        <div className="lg:col-span-4">
          <p className="font-tag text-xl">For the solo episodes.</p>
          <p className="mt-3 leading-relaxed opacity-85">
            Got a topic Sav should rant about solo? Drop it. The community votes — the loudest ideas
            make the cut.
          </p>
        </div>
      </div>

      <KineticMarquee phrases={["pitch it", "vote it up", "watch it happen"]} />

      <div className="mt-16 grid lg:grid-cols-12 gap-12">
        {/* Submit form */}
        <form
          onSubmit={submit}
          data-testid="suggest-form"
          className="lg:col-span-5 border-2 border-[var(--tsws-green)] bg-[var(--tsws-paper)] p-6 lg:p-8 self-start sticky lg:top-28 shadow-brutal"
        >
          <div className="flex items-center gap-3 mb-5">
            <Lightbulb className="text-[var(--tsws-cigar)]" />
            <h2 className="font-display text-3xl">drop an idea.</h2>
          </div>

          <label className="block mb-4">
            <span className="block font-label text-xs mb-2 opacity-80">Your name (optional)</span>
            <input
              data-testid="suggest-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              maxLength={120}
              className="brutal-input"
              placeholder="Anonymous"
            />
          </label>

          <label className="block mb-4">
            <span className="block font-label text-xs mb-2 opacity-80">Idea title *</span>
            <input
              data-testid="suggest-title"
              required
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              maxLength={200}
              className="brutal-input"
              placeholder="e.g. Sav reviews every Aussie pub he's been kicked out of"
            />
          </label>

          <label className="block mb-4">
            <span className="block font-label text-xs mb-2 opacity-80">Details (optional)</span>
            <textarea
              data-testid="suggest-body"
              rows={3}
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              maxLength={2000}
              className="brutal-input resize-y"
              placeholder="A sentence or two on why it'd slap."
            />
          </label>

          <label className="block mb-6">
            <span className="block font-label text-xs mb-2 opacity-80">Category</span>
            <select
              data-testid="suggest-category"
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              className="brutal-input"
            >
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>

          <button
            type="submit"
            data-testid="suggest-submit"
            disabled={submitting || !form.title.trim()}
            className="w-full font-display text-lg px-6 py-4 border-2 border-[var(--tsws-green)] bg-[var(--tsws-green)] text-[var(--tsws-orange)] hover-brutal hover:bg-[var(--tsws-cigar)] hover:border-[var(--tsws-cigar)] inline-flex items-center justify-center gap-3 disabled:opacity-60"
          >
            {submitting ? "sending..." : "submit idea"} <Send size={16} />
          </button>
        </form>

        {/* List */}
        <div className="lg:col-span-7">
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-6" data-testid="suggest-filters">
            <button
              data-testid="filter-all"
              onClick={() => setFilter("all")}
              className={`font-display text-base px-4 py-2 border-2 border-[var(--tsws-green)] ${
                filter === "all"
                  ? "bg-[var(--tsws-green)] text-[var(--tsws-orange)]"
                  : "text-[var(--tsws-green)] hover:bg-[var(--tsws-green)]/10"
              }`}
            >
              all
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`filter-${c.id}`}
                onClick={() => setFilter(c.id)}
                className={`font-display text-base px-4 py-2 border-2 border-[var(--tsws-green)] ${
                  filter === c.id
                    ? "bg-[var(--tsws-green)] text-[var(--tsws-orange)]"
                    : "text-[var(--tsws-green)] hover:bg-[var(--tsws-green)]/10"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {loading && (
            <div className="font-label text-sm opacity-60">Loading ideas...</div>
          )}

          {!loading && filtered.length === 0 && (
            <div
              data-testid="suggest-empty"
              className="border-2 border-dashed border-[var(--tsws-green)] p-10 text-center"
            >
              <Lightbulb className="mx-auto text-[var(--tsws-cigar)]" size={36} />
              <p className="font-display text-3xl mt-3">no ideas yet.</p>
              <p className="font-tag opacity-80 mt-1">Be the first to pitch one.</p>
            </div>
          )}

          <ul className="space-y-4" data-testid="suggest-list">
            {filtered.map((it, idx) => {
              const hasVoted = voted.has(it.id);
              return (
                <li
                  key={it.id}
                  data-testid={`suggest-item-${it.id}`}
                  className="border-2 border-[var(--tsws-green)] bg-[var(--tsws-paper)] p-5 flex gap-5 items-start hover-brutal"
                >
                  <button
                    data-testid={`upvote-${it.id}`}
                    onClick={() => upvote(it.id)}
                    disabled={hasVoted}
                    aria-label="Upvote"
                    className={`shrink-0 w-16 flex flex-col items-center justify-center py-2 border-2 border-[var(--tsws-green)] transition-colors ${
                      hasVoted
                        ? "bg-[var(--tsws-green)] text-[var(--tsws-orange)] cursor-default"
                        : "bg-[var(--tsws-orange-light)] text-[var(--tsws-green)] hover:bg-[var(--tsws-green)] hover:text-[var(--tsws-orange)]"
                    }`}
                  >
                    <ChevronUp size={22} strokeWidth={2.8} />
                    <span className="font-display text-2xl leading-none mt-1">
                      {it.upvotes || 0}
                    </span>
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-display text-xs px-2 py-0.5 bg-[var(--tsws-green)] text-[var(--tsws-orange)] uppercase">
                        {CATEGORIES.find((c) => c.id === it.category)?.label || it.category}
                      </span>
                      {idx === 0 && filter === "all" && (it.upvotes || 0) > 0 && (
                        <span className="font-label text-xs text-[var(--tsws-cigar)]">⏤ top idea</span>
                      )}
                    </div>
                    <h3 className="font-display text-2xl leading-tight">
                      {it.title.toLowerCase()}
                    </h3>
                    {it.body && (
                      <p className="mt-2 leading-relaxed opacity-90">{it.body}</p>
                    )}
                    <p className="mt-3 font-tag text-sm opacity-70">
                      — {it.name?.trim() || "anonymous"}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <style>{`
        .brutal-input {
          width: 100%;
          background: var(--tsws-orange);
          color: var(--tsws-green);
          border: 2px solid var(--tsws-green);
          padding: 12px 14px;
          font-family: 'Fraunces', Georgia, serif;
          font-size: 15px;
          outline: none;
          transition: background 200ms ease, color 200ms ease, box-shadow 200ms ease;
        }
        .brutal-input::placeholder { color: var(--tsws-green); opacity: 0.45; }
        .brutal-input:focus {
          background: var(--tsws-green);
          color: var(--tsws-orange);
          box-shadow: 4px 4px 0 0 var(--tsws-cigar);
        }
      `}</style>
    </div>
  );
}
